import moment from 'moment';
import 'moment-timezone';
import { ensureCurrentUser } from './data';
import { createImageVariantConfig, types as sdkTypes } from '../util/sdkLoader';

import { v4 as uuidv4 } from 'uuid';
import { formatMoney } from './currency';
import { LISTING_TYPE } from './enums';
import { toast } from 'react-toastify';
const { Money } = sdkTypes;
const { UUID } = sdkTypes;

export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const getClientSecret = setupIntent => setupIntent?.attributes?.clientSecret || null;

export const getPaymentParams = (currentUser, formValues) => {
  const { name, addressLine1, addressLine2, postal, state, city, country } = formValues || {};

  const addressMaybe =
    addressLine1 && postal
      ? {
        address: {
          city: city,
          country: country,
          line1: addressLine1,
          line2: addressLine2,
          postal_code: postal,
          state: state,
        },
      }
      : {};

  const billingDetails = {
    name,
    email: ensureCurrentUser(currentUser).attributes.email,
    ...addressMaybe,
  };

  const paymentParams = {
    payment_method_data: {
      billing_details: billingDetails,
    },
  };

  return paymentParams;
};

export function calculateActualTotal(lineItems, isStripeBooking, isDeposit) {
  let totalAmount = 0;

  if (isStripeBooking && isDeposit) {
    for (const item of lineItems) {
      const code = item.code || '';
      const amount = item.lineTotal?.amount || 0;
      if (code === 'line-item/deposit-amount' || code === 'line-item/remaining-payment') {
        totalAmount += amount;
      }
    }


  } else {
    for (const item of lineItems) {
      const code = item.code || '';
      const amount = item.lineTotal?.amount || 0;
      if (code !== 'line-item/deposit-amount') {
        totalAmount += amount;
      }
    }
  }

  return totalAmount; // in minor units like pence
}

export const splitDatesRangeInfoBy365Limit = (startOfDay, endOfDay) => {
  const todayPlus365 = moment()
    .add(365, 'days')
    .startOf('day');

  // Case 1: Entire range is within the 365-day window
  if (endOfDay.isSameOrBefore(todayPlus365)) {
    return {
      inRange: {
        start: startOfDay.toDate(),
        end: endOfDay.toDate(),
      },
      outRange: {
        start: null,
        end: null,
      },
    };
  }

  // Case 2: Entire range is after the 365-day window
  if (startOfDay.isAfter(todayPlus365)) {
    return {
      inRange: {
        start: null,
        end: null,
      },
      outRange: {
        start: moment(startOfDay).valueOf(), // ensures it's a moment object and converts to Unix (seconds)
        end: moment(endOfDay).valueOf(), // clone + start of day in Unix
      },
    };
  }

  // Case 3: Range spans across the 365-day limit
  const inRangeEnd = todayPlus365;
  const outRangeStart = todayPlus365
    .clone()
    .add(1, 'day')
    .startOf('day');

  return {
    inRange: {
      start: startOfDay.toDate(),
      end: inRangeEnd.toDate(),
    },
    outRange: {
      start: moment(outRangeStart).valueOf(), // ✅ milliseconds
      end: moment(endOfDay).valueOf(), // ✅ milliseconds
    },
  };
};

export function transformToAvailabilityExceptions(existingExceptionOptions) {
  return existingExceptionOptions?.map(exception => {
    return {
      id: {
        _sdkType: 'UUID',
        uuid: exception?.id, // You can replace this with an actual UUID generator
      },
      type: 'availabilityException',
      attributes: {
        start: new Date(exception.start), // Convert from Unix timestamp to Date
        end: new Date(exception.end),
        seats: parseInt(exception.seats, 10),
      },
    };
  });
}

function normalizeDate(date) {
  return new Date(date).toISOString();
}

export function splitExceptionByDateRange(exception, existingExceptionOptions = []) {
  const { start, end, seats } = exception.attributes;
  const id = exception.id?.uuid || null;
  const exceptionStartISO = normalizeDate(start);
  const exceptionEndISO = normalizeDate(end);

  let matchedIndex = -1;

  const matched = existingExceptionOptions.find((opt, index) => {
    const optStartISO = normalizeDate(opt.start);
    const optEndISO = normalizeDate(opt.end);
    const isMatch = optStartISO === exceptionStartISO && optEndISO === exceptionEndISO;
    if (isMatch) matchedIndex = index;
    return isMatch;
  });

  if (matched && matchedIndex !== -1) {
    // Remove matched entry from existing options
    const updatedOptions = [...existingExceptionOptions];
    updatedOptions.splice(matchedIndex, 1); // remove matched one

    return {
      outRangeExection: {
        id,
        start: matched.start,
        end: matched.end,
        seats: parseInt(matched.seats, 10),
      },
      updatedExistingOptions: updatedOptions,
    };
  }

  return {
    inRangeData: {
      id,
      start,
      end,
      seats,
    },
  };
}


export const filterAvailableBookingSlots = (slots = []) => {
  const now = Date.now();

  return slots
    .filter(slot => {
      const hasSeats = Number(slot.seats) > 0;
      const isInFuture = Number(slot.start) > now;
      return hasSeats && isInFuture;
    })
    .sort((a, b) => Number(a.start) - Number(b.start));
};

export function getUpdatedBookingDataArr(exception, existingBookingDataArr = []) {
  const exceptionStart = moment(exception.attributes.start).startOf('day');
  const exceptionEnd = moment(exception.attributes.end).endOf('day');

  const updatedBookingDataArr = (existingBookingDataArr || []).filter(slot => {
    const slotStart = moment(slot.start);
    const slotEnd = moment(slot.end);

    // Check if the slot overlaps with the exception
    const isOverlapping =
      slotStart.isBetween(exceptionStart, exceptionEnd, undefined, '[]') ||
      slotEnd.isBetween(exceptionStart, exceptionEnd, undefined, '[]') ||
      exceptionStart.isBetween(slotStart, slotEnd, undefined, '[]');

    return !isOverlapping; // keep slots that do NOT overlap
  });

  return updatedBookingDataArr;
}

export const isWithin366Days = endDate => {
  const now = moment(); // current moment
  const diffInDays = endDate.diff(now, 'days');
  return diffInDays >= 0 && diffInDays <= 366;
};

export const formatDateRange = (start, end) => {
  const startDate = moment(start);
  // const endDate = moment(end);
  return `${startDate.format('D MMM, YYYY')}`;
};

export const getListingType = listing => {
  return listing?.attributes?.publicData?.listingType || 'Unknown'; // Default to 'Unknown' if listingType is not available
};

export const filterTabsByListingType = (listingType, tabs) => {
  if (
    listingType === LISTING_TYPE.NATURE_ACTIVITY ||
    listingType === LISTING_TYPE.ONEDAY_ACTIVITY
  ) {
    return tabs;
  } else {
    return tabs.filter(tab => tab !== 'availability');
  }
};

export function retrieveUserFavorites(user) {
  if (!user || !user.id) {
    return null;
  }
  const protectedData = user.attributes?.profile?.protectedData || {};
  return protectedData.favorites ?? [];
}

export const isWindowDefined = () => {
  return typeof window !== 'undefined';
};


export const cardRenderSizes = isMapVariant => {
  if (isMapVariant) {
    const panelMediumWidth = 50;
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 767px) 100vw',
      `(max-width: 1023px) ${panelMediumWidth}vw`,
      `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
      `${panelLargeWidth / 3}vw`,
    ].join(', ');
  } else {
    const panelMediumWidth = 50;
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 549px) 100vw',
      '(max-width: 767px) 50vw',
      `(max-width: 1439px) 26vw`,
      `(max-width: 1920px) 18vw`,
      `14vw`,
    ].join(', ');
  }
};


export const getUserType = user => {
  return user?.attributes?.profile?.publicData?.userType;
};


export const formatBookingDates = bookingDataArr => {
  if (!Array.isArray(bookingDataArr)) return [];

  return bookingDataArr.map(({ start, end }) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const startDay = startDate.getDate().toString().padStart(2, '0');
    const endDay = endDate.getDate().toString().padStart(2, '0');

    const startMonth = (startDate.getMonth() + 1).toString().padStart(2, '0');
    const endMonth = (endDate.getMonth() + 1).toString().padStart(2, '0');

    // If start and end months are the same, only show once
    if (startMonth === endMonth) {
      return `${startDay}–${endDay}/${startMonth}`;
    } else {
      return `${startDay}/${startMonth}–${endDay}/${endMonth}`;
    }
  });
};

export const filterListingsByBookingDates = (listings, dateRange) => {
  if (!listings || !Array.isArray(listings)) return [];

  const [startStr, endStr] = dateRange?.split(',') || [];
  if (!startStr || !endStr) return listings;

  const queryStart = new Date(startStr);
  const queryEnd = new Date(endStr);

  return listings.filter(listing => {
    const bookingDataArr = listing?.attributes?.publicData?.bookingDataArr || [];
    return bookingDataArr.some(booking => {
      const bookingStart = new Date(booking.start);
      const bookingEnd = new Date(booking.end);
      return (
        bookingStart.getTime() <= queryEnd.getTime() &&
        bookingEnd.getTime() >= queryStart.getTime()
      );
    });
  });
};


export const showToaster = (type, message, options = {}) => {
  const defaultOptions = {
    position: 'top-center',
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'light',
    onClose: () => { }, // Placeholder onClose function
    ...options, // Merge any additional options passed to the function
  };

  let redirectTimeout;

  const handleToastClose = () => {
    clearTimeout(redirectTimeout); // Clear the timeout when the toast is closed
    defaultOptions.onClose(); // Call the original onClose function if provided
  };

  // Display the toast based on the type
  if (type === 'success') {
    toast.success(message, {
      ...defaultOptions,
      onClose: handleToastClose,
    });
  } else if (type === 'error') {
    toast.error(message, {
      ...defaultOptions,
      onClose: handleToastClose,
    });
  } else {
    console.warn('showToast called with invalid type. Expected "success" or "error".');
    return;
  }

  // Redirect automatically after a certain duration if redirectUrl is provided
  if (options.redirectUrl) {
    redirectTimeout = setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.location.href = options.redirectUrl;
      }
    }, defaultOptions.autoClose);
  }
};

export const getSearchParams = () => {
  const aspectWidth = 1;
  const aspectHeight = 1;
  const variantPrefix = 'listing-card';
  const aspectRatio = aspectHeight / aspectWidth;

  return {
    page: 1,
    perPage: 100,
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      'publicData',
    ],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };
};



export const filterOutBlogSection = sections => {
  if (!Array.isArray(sections)) return [];

  return sections.filter(section => section.sectionName !== 'Blog');
};



export const getUserSubscriptionBadge = listing => {
  return (
    listing?.author?.attributes?.profile?.publicData?.subscriptionBadge || null
  );
};

export const normalizeFutureExceptions = exceptions => {
  return exceptions.map(ex => ({
    id: ex.id,
    attributes: {
      start: new Date(ex.start),
      end: new Date(ex.end),
      seats: ex.seats,
    },
  }));
};


export const splitAvailabilityBy365Days = ({
  start,
  end,
  seats,
}) => {

  // ⭐ normalize EXACTLY like saved exceptions
  const boundary = moment()
    .startOf('day')
    .add(365, 'days');

  const sharetribeExceptions = [];
  const futureExceptions = [];
  const sharetribeFormatedExceptions = [];

  const rangeStart = moment(start)
    .startOf('day')
    .seconds(0)
    .milliseconds(0);

  const rangeEnd = moment(end)
    .startOf('day')
    .seconds(0)
    .milliseconds(0);

  // ✅ fully inside
  if (rangeEnd.isSameOrBefore(boundary)) {

    sharetribeExceptions.push({
      start: rangeStart.toDate(),
      end: rangeEnd.toDate(),
      seats,
    });
    sharetribeFormatedExceptions.push({
      id: uuidv4(),
      start: rangeStart.toISOString(),
      end: rangeEnd.toISOString(),
      seats,
    });

    return { sharetribeExceptions, futureExceptions,sharetribeFormatedExceptions };
  }

  // ✅ fully future
  if (rangeStart.isSameOrAfter(boundary)) {

    futureExceptions.push({
      id: uuidv4(),
      start: rangeStart.toISOString(),
      end: rangeEnd.toISOString(),
      seats,
    });

    return { sharetribeExceptions, futureExceptions ,sharetribeFormatedExceptions };
  }

  // ✅ split cleanly (NO overlaps)
  sharetribeExceptions.push({
    start: rangeStart.toDate(),
    end: boundary.toDate(),
    seats,
  });

  sharetribeFormatedExceptions.push({
    id: uuidv4(),
    start: rangeStart.toISOString(),
    end: boundary.toISOString(),
    seats,
  });

  futureExceptions.push({
    id: uuidv4(),
    start: boundary.toISOString(),
    end: rangeEnd.toISOString(),
    seats,
  });

  return { sharetribeExceptions, futureExceptions ,sharetribeFormatedExceptions};
};


export const futureExceptionsToTimeSlots = (futureExceptions = []) => {
  return futureExceptions.map(ex => ({

    id: new UUID(ex.id),
    type: 'timeSlot',

    attributes: {
      start: moment(ex.start)
        .startOf('day')
        .seconds(0)
        .milliseconds(0)
        .toDate(),

      end: moment(ex.end)
        .startOf('day')
        .seconds(0)
        .milliseconds(0)
        .toDate(),

      seats: ex.seats,
      type: 'time-slot/time',
    },

  }));
};



export const getQueryParams = (url, params = []) => {
  try {
    // Fix malformed query strings with multiple '?'
    const fixedUrl = url.replace(/\?(?=.*\?)/, '&'); // Replace subsequent `?` with `&`

    const urlParams = new URL(fixedUrl).searchParams;

    // Build an object dynamically with all specified parameters
    const result = {};
    params.forEach((param) => {
      const paramValue = urlParams.get(param);
      if (paramValue === 'true') {
        result[param] = true;
      } else if (paramValue === 'false') {
        result[param] = false;
      } else {
        result[param] = paramValue;
      }
    });

    return result;
  } catch (error) {
    console.error('Error parsing URL:', error);
    return null; // Return null if URL parsing fails
  }
};

export const getListingCurrency = listing => {
  if (!listing || !listing?.id) {
    return 'EUR';
  }
  return listing?.attributes?.price?.currency;
};

export const getListingPrice = listing => {
  if (!listing || !listing?.id) {
    return 'EUR';
  }
  return listing?.attributes?.price?.amount/100;
};

export const getListingTitle = listing => {
  if (!listing || !listing?.id) {
    return null;
  }
  return listing?.attributes?.title;
};

export function calculatePrice(transaction, transitionName) {
  if (!transaction) return 0;

  const depositPercentage =
    transaction?.listing?.attributes?.publicData?.depositPercentage ?? 0;

  const payinTotal =
    transaction?.attributes?.payinTotal?.amount ?? 0;

  // Safe calculations
  const depositAmount = Math.round(
    (payinTotal * depositPercentage) / 100
  );

  const remainingAmount = Math.max(payinTotal - depositAmount, 0);

  /*
      TRANSITIONS:

      transition/issue-deposit-payment-link
      transition/issue-rest-payment-link
      transition/issue-full-payment-link
  */

  switch (transitionName) {

    case 'transition/issue-deposit-payment-link':
      // If no deposit configured → fallback to full
      return depositPercentage > 0 ? depositAmount : payinTotal;

    case 'transition/issue-rest-payment-link':
      return remainingAmount;

    case 'transition/issue-full-payment-link':
      return payinTotal;

    default:
      // Smart fallback
      return payinTotal;
  }
}
